import React from 'react';
import { graphql, ChildProps } from 'react-apollo';
import I18n from 'i18n-js';
import { get, some, differenceBy, compact, isEqual } from 'lodash';

import {
  combinedMarketplaceSearchQuery,
  mapUrlParams,
  PageSettings,
  includePromotedRows,
  updateCategoryAsParams,
} from './map_params';
import ListingGrid from './listing_grid';
import FooterRow from './footer_row';
import { getPage, getTotalPages, getOffsetFromPage } from '../../pagination_helpers';

import {
  CoreMarketplaceCombinedMarketplaceSearch,
  reverb_search_ListingsSearchRequest_Aggregation,
  core_apimessages_CollectionHeader_CollectionType,
  Ad,
  reverb_search_ListingsSearchRequest_Sort,
} from '../../schemas/rql';
import FollowButtonContainer from '../discovery/feed/follow_button_container';
import MarketplaceCSPRow from '../discovery/csp/marketplace_csp_row';
import SidebarAds from '../discovery/facets/sidebar_ads';
import { MarketplacePathContext } from '../shared/url_helpers';
import { renameRqlParam } from '../shared/rql_param_helper';
import experiments from '@reverbdotcom/commons/src/experiments';
import CurationModal from '../discovery/curation/modal';
import CombinedBumpListingGrid from './combined_bump_listing_grid';
import HeaderWithMetadata from './header_with_metadata';

import RQLSearchGrid from '@reverbdotcom/commons/src/components/rql_search_grid';
import ScrollTarget from '@reverbdotcom/commons/src/components/scroll_target';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { renameParams } from '@reverbdotcom/commons/src/facet_utils';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { UpdateURLProvider } from '@reverbdotcom/commons/src/components/with_update_url';
import { MParticlePageName, MParticleEventName, trackPageView, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { adBannerMparticleData } from '@reverbdotcom/commons/src/components/ads/ad_mparticle_tracking_helpers';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { browserHistory } from 'react-router';
import { Location } from 'history';

import MarketplaceFallbackListings from './marketplace_fallback_listings';
import * as elog from '@reverbdotcom/commons/src/elog';

import SearchFeedback from './search_feedback';
import { AdBanner } from '@reverbdotcom/commons/src/components/ads/ad_banner';
import { trackCriteoProductList } from '@reverbdotcom/commons/src/criteo';
import LowResultsAlert from '../low_results_alert';
import NoResultsAlert from '../no_results_alert';
import { SubqueryRows } from '../subquery_rows';

interface RouterLocationProps {
  location: Location;
  params: MarketplacePathContext;
}

interface IExternalProps extends RouterLocationProps {
  emptyGrid?: JSX.Element;
  hideFooter?: boolean;
  hideFollowButtons?: boolean;
  pageSettings: PageSettings;
  renderCollectionHeaderDescription?: boolean;
  skipHeader?: boolean;
  pageName?: MParticlePageName;
  ad?: Ad;
  loading?: boolean;
}

type IUserProps = IExternalProps & IUserContext;

type IProps = ChildProps<IUserProps, CoreMarketplaceCombinedMarketplaceSearch.Query, CoreMarketplaceCombinedMarketplaceSearch.Variables>;

const BUMP_LIMIT = 15;
const LOW_LISTINGS_THRESHOLD = 10;

export class MarketplacePage extends React.Component<IProps, null> {
  scrollTarget: ScrollTarget;

  componentDidMount() {
    if (this.props.pageName === MParticlePageName.Marketplace) {
      this.trackAdExperimentQualifyingEvent();
    }
  }

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.location.query.page !== this.props.location.query.page) {
      this.scrollTarget.scrollToTop();
    }

    if (this.loading()) { return; }

    if (prevProps.data.listingsSearch !== this.props.data.listingsSearch) {
      this.trackPageView();
      if (this.props.pageName === MParticlePageName.Marketplace) {
        this.trackAdExperimentQualifyingEvent();
      }
    }

    if (this.props.location.query.brand_model_redirected === 'true') {
      this.stripRedirectParam('brand_model_redirected');
    }

    if (this.props.location.query.brand_redirected === 'true') {
      this.stripRedirectParam('brand_redirected');
    }
  }

  stripRedirectParam(param) {
    const { location } = this.props;
    delete location.query[param];
    browserHistory.push(location);
  }

  loading() {
    return this.props.loading || !this.props.data || this.props.data.loading;
  }

  searchResult(): CoreMarketplaceCombinedMarketplaceSearch.ListingsSearch {
    return this.props.data?.listingsSearch;
  }

  aggsResult(): CoreMarketplaceCombinedMarketplaceSearch.AggsSearch {
    return this.props.data?.aggsSearch;
  }

  bumpSearchResult(): CoreMarketplaceCombinedMarketplaceSearch.ListingsSearch {
    return this.props.data?.bumpedSearch;
  }

  shouldShowCombinedShippingCheckbox() {
    return this.props.params.shop;
  }

  filters() {
    const aggs = this.aggsResult();
    if (!aggs) { return []; }
    let { filters } = aggs;

    // Removes TRAITS filter if user isn't rolled out.
    if (!isExperimentEnabled(this.props.user, experiments.TRAIT_FACETS)) {
      filters = filters.filter(f =>
        f.aggregationName !== reverb_search_ListingsSearchRequest_Aggregation.TRAITS);
    }

    // Removes the 'combined_shipping' option from the `Show Only` || `Find a Deal` filter unless rolled out user is visiting shop page
    if (!this.shouldShowCombinedShippingCheckbox()) {
      filters = filters.map((filter) => {
        if (filter.key !== 'SHOW_ONLY' && filter.key !== 'FIND_A_DEAL') { return filter; }

        return {
          ...filter,
          options: filter.options.filter((o) => o.paramName !== 'combined_shipping'),
        };
      });
    }
    // Removes the sidebar keyword search when it is a brand page
    if (this.isBrandPage()) {
      filters = filters.filter(filter => filter.key !== 'TEXT_QUERY');
    }

    return renameParams(filters, renameRqlParam);
  }

  getTotalText() {
    const count = get(this.props, 'data.listingsSearch.total', 0);

    return I18n.t(
      'discovery.resultsCount.countListings',
      {
        count,
        num: I18n.toNumber(count, { precision: 0 }),
      },
    );
  }

  getFollowParams() {
    return { ...this.props.params, ...this.props.location.query };
  }

  getListings() {
    return this.searchResult()?.listings || [];
  }

  getBumpedListings() {
    return this.bumpSearchResult()?.listings || [];
  }

  getFallbackListings() {
    const fallback = this.searchResult()?.fallbackListings || [];
    const listings = this.getListings();

    if (listings.length === 0) return fallback;

    return differenceBy(fallback, listings, 'id');
  }

  getSuggestedQuery() {
    const queries = this.searchResult()?.suggestedQueries || [];

    return queries?.[0];
  }

  shouldShowFallbackListings() {
    return this.getFallbackListings().length > 0;
  }

  hideProductRow() {
    if (this.props.pageSettings.holidaySale) return true;
    if (this.props.params.sale_slug) return true;

    return some([
      'on_sale',
      'handmade',
      'free_shipping',
      'free_expedited_shipping',
      'show_only_sold',
      'accepts_offers',
      'accepts_gift_cards',
      'preferred_seller',
      'zero_percent_financing',
      'csp_slug',
      'combined_shipping',
    ], (item) => {
      return this.props.location.query[item];
    });
  }

  renderRelatedSearchRow() {
    if (this.hideProductRow()) {
      return null;
    }

    return (
      <MarketplaceCSPRow
        title={I18n.t('discovery.cspRowTitle')}
        location={this.props.location}
        routeParams={this.props.params}
        trackingQuery={this.trackingQuery()}
      />);
  }

  renderFollowBanner() {
    if (this.props.hideFollowButtons) return null;
    if (this.loading()) return null;
    if (this.searchResult() && !this.searchResult().total) return null;
    if (this.shouldShowFallbackListings()) return null;

    return (
      <FollowButtonContainer
        followParams={this.getFollowParams()}
        renderWithBanner
        disabled={!!this.props.location.query.trait_values}
        ignoreFollowPrompt
      />
    );
  }

  renderAdBanner() {
    const hasData = (!this.props.loading && !!this.props.ad?.imageAdCreative);
    if (!hasData) return null;
    // do not show ad banner if Shop Page
    if (this.props.pageName === MParticlePageName.Shop) return null;
    // do not show on Marketplace Page if user is in MARKETPLACE_GRID_ADS exp
    if (isExperimentEnabled(this.props.user, experiments.MARKETPLACE_GRID_ADS) && this.props.pageName === MParticlePageName.Marketplace) return null;

    return (
      <div className="mt-3 mb-2 site-wrapper">
        <AdBanner
          ad={this.props.ad}
        />
      </div>
    );
  }

  shouldShowCombinedListingGrid() {
    if (this.shouldShowFallbackListings()) return null;
    if (this.getListings().length === 0) return null;

    return includePromotedRows(this.props.params, this.props.location?.query);
  }

  didYouMeanSuggestion() {
    return this.searchResult()?.suggestions[0]?.text || null;
  }

  onlyQuery() {
    return isEqual(Object.keys(this.props.location.query), ['query']);
  }

  useExperimentalRecall() {
    return this.props.pageSettings.useExperimentalRecall &&
      isExperimentEnabled(this.props.user, experiments.SEARCH_RECALL_IMPROVEMENTS);
  }

  emptyGridExperimentOverride() {
    if (this.useExperimentalRecall() && this.onlyQuery()) {
      return (
        <NoResultsAlert
          query={this.props.location?.query.query}
          suggestion={this.didYouMeanSuggestion()}
        />
      );
    }

    // overrides default empty grid if either experiment is active
    if (this.shouldShowFallbackListings()) return <div />;

    // uses default
    return this.props.emptyGrid;
  }

  hasLowResults() {
    return !this.props.data?.loading &&
      this.getListings().length <= LOW_LISTINGS_THRESHOLD;
  }

  hasNoResults() {
    return !this.props.data?.loading &&
      this.getListings().length === 0;
  }

  lowResultsAlert() {
    if (this.onlyQuery() &&
        this.hasLowResults() &&
        !this.hasNoResults() &&
        this.useExperimentalRecall()) {
      return (
        <LowResultsAlert
          suggestion={this.didYouMeanSuggestion()}
          query={this.props.location?.query.query}
        />
      );
    }

    return null;
  }

  gridAd(): Ad {
    if (isExperimentEnabled(this.props.user, experiments.MARKETPLACE_GRID_ADS) && this.props.pageName === MParticlePageName.Marketplace) {
      return this.props.ad;
    }
    return null;
  }

  renderCards() {
    if (this.shouldShowCombinedListingGrid()) {
      return (
        <CombinedBumpListingGrid
          error={!!this.props.data.error}
          listings={this.getListings()}
          bumpedListings={this.getBumpedListings()}
          loading={this.loading()}
          trackingQuery={this.trackingQuery()}
          ad={this.gridAd()}
          pageSettings={this.props.pageSettings}
        />
      );
    }

    return (
      <ListingGrid
        loading={this.loading()}
        error={!!this.props.data.error}
        listings={this.getListings()}
        showingSold={this.props.location.query.show_only_sold}
        emptyGrid={this.emptyGridExperimentOverride()}
        redesignedCard={isExperimentEnabled(this.props.user, experiments.REDESIGNED_LISTING_CARD)}
        trackingQuery={this.trackingQuery()}
        pageSettings={this.props.pageSettings}
      />
    );
  }

  renderFallbackCards() {
    return (
      <MarketplaceFallbackListings
        fallbackListings={this.getFallbackListings()}
        query={this.props.location.query.query}
        hasPrimaryListings={this.getListings().length > 0}
        showingSold={this.props.location.query.show_only_sold}
        displayAsRow={this.useExperimentalRecall()}
      />
    );
  }

  renderSubqueryRows() {
    if (!this.useExperimentalRecall() ||
      !this.onlyQuery() ||
      !this.hasLowResults()) return null;

    const { query } = this.props.location.query;

    return (
      <SubqueryRows
        query={query}
        location={this.props.location}
        params={this.props.params}
        listingsSearchCount={this.getListings().length}
      />
    );
  }

  trackingQuery() {
    return this.props.location.query.brand_redirected === 'true' ? this.props.location.query.make : this.props.location.query.query;
  }

  trackSearchRecallImprovementsExperiment() {
    if (this.props.location.pathname?.includes('/marketplace') && this.searchResult().total <= LOW_LISTINGS_THRESHOLD) {
      return experiments.SEARCH_RECALL_IMPROVEMENTS;
    }
    return null;
  }

  sharedTrackingData() {
    const results = this.searchResult();
    const trackedExperiments = [
      experiments.LISTING_CARD_LOCATION,
      experiments.BOOST_MARKETPLACE_SELLER_LOCATION_V3,
      experiments.REDESIGNED_LISTING_CARD,
      experiments.DUMMY,
      experiments.MARKETPLACE_GRID_ADS,
      experiments.LISTINGS_QUERY_REWRITE_V2,
      experiments.MARKETPLACE_DISMISSIBLE_FILTERS,
      experiments.LISTINGS_SEARCH_BEST_MATCH_SIGNALS_V3,
      experiments.LISTINGS_SEARCH_LIKELIHOOD_TO_SELL,
      experiments.AA_MARKETPLACE_SESSION_BUCKETING,
      this.trackSearchRecallImprovementsExperiment(),
    ];

    return {
      pageName: this.props.pageName || MParticlePageName.Marketplace,
      experiments: formatExperimentsForEventAttributes(this.props.user, compact(trackedExperiments)),
      query: this.trackingQuery(),
      brandRedirected: this.props.location.query.brand_redirected === 'true',
      brandModelRedirected: this.props.location.query.brand_model_redirected === 'true',
      sort: this.props.location?.query?.sort || reverb_search_ListingsSearchRequest_Sort.NONE,
      listingIds: results.listings?.map(l => l.id),
      querySuggestion: this.getSuggestedQuery(),
      page: getPage(results.offset, results.limit),
      totalPages: getTotalPages(results.total, results.limit),
      listingsCount: results.total,
      fallbackListingsCount: this.getFallbackListings().length,
      productType: this.props.location.query.product_type || '',
      make: this.props.location.query.make || '',
      category: this.props.location.query.category || '',
      shopSlug: this.props.params.shop || '',
      brand: this.props.params.brand_slug || '',
      curatedSetSlug: this.props.params.curated_set_slug || '',
      postalCode: this.props.location.query.postal_code,
      distance: this.props.location.query.distance,
    };
  }

  trackPageView() {
    trackPageView({
      ...this.sharedTrackingData(),
    });

    const results = this.searchResult();

    trackCriteoProductList({
      listingIds: results.listings.map(l => l.id),
      keywords: this.trackingQuery(),
      page_number: getPage(results.offset, results.limit),
    }, this.props.user);
  }

  trackAdExperimentQualifyingEvent() {
    trackEvent({
      ...adBannerMparticleData(this.props.ad),
      ...{
        componentName: MParticlePageName.Marketplace,
        eventName: MParticleEventName.MarketplaceGridAdQE,
        experiments: formatExperimentsForEventAttributes(this.props.user, [experiments.MARKETPLACE_GRID_ADS]),
      },
    });
  }

  isBrandPage(): boolean {
    return this.props.pageSettings.collectionType === core_apimessages_CollectionHeader_CollectionType.Brand;
  }

  renderSellerLocationFilter(): boolean {
    const { pathname } = this.props.location;
    return pathname && !pathname.includes('/shop');
  }

  render() {
    const searchResult = this.searchResult();

    if (this.props.data.error) {
      elog.error(
        'marketplace-render-failure',
        {
          networkErrorStack: this.props.data.error.networkError?.stack?.substring(0, 500),
        },
        this.props.data.error,
      );
    }

    return (
      <UpdateURLProvider updateUrl={updateCategoryAsParams}>
        {!this.props.skipHeader &&
          <HeaderWithMetadata
            params={this.props.params}
            query={get(this.props, 'location.query', {})}
            pageSettings={this.props.pageSettings}
            renderCollectionHeaderDescription={this.props.renderCollectionHeaderDescription}
            isBrandPage={this.isBrandPage()}
          />
        }
        <ScrollTarget onlyScrollUp ref={r => this.scrollTarget = r} />
        <RQLSearchGrid
          error={!!this.props.data.error}
          loading={this.loading()}
          search={searchResult}
          filters={this.filters()}
          location={this.props.location}
          aboveGridBanner={this.renderAdBanner()}
          relatedSearchRow={this.renderRelatedSearchRow()}
          totalText={this.getTotalText()}
          followButton={!this.props.hideFollowButtons &&
            <FollowButtonContainer
              followParams={this.getFollowParams()}
              disabled={!!this.props.location.query.trait_values}
            />
          }
          cards={this.renderCards()}
          fallbackCards={this.renderFallbackCards()}
          subqueryRows={this.renderSubqueryRows()}
          hasPagination
          hasFacets
          hasSearchOverview
          sidebarContent={<SidebarAds region={this.props.user.shippingRegionCode} />}
          followBanner={this.renderFollowBanner()}
          renderSellerLocationFilter={this.renderSellerLocationFilter()}
          trackingQuery={this.trackingQuery()}
          lowResultsAlert={this.lowResultsAlert()}
          useExperimentalRecall={this.useExperimentalRecall()}
          queryParams={this.props.location.query}
        />
        {!this.loading() &&
          <SearchFeedback
            isBrandPage={this.isBrandPage()}
            searchParams={this.props.location.search}
            trackingProps={this.sharedTrackingData()}
          />
        }
        {!this.props.hideFooter &&
          <FooterRow
            pageName={this.props.pageName}
            queryParams={this.props.location.query}
            search={searchResult}
          />
        }
        <CurationModal />
      </UpdateURLProvider>
    );
  }
}

const connect = graphql<
IUserProps,
CoreMarketplaceCombinedMarketplaceSearch.Query,
CoreMarketplaceCombinedMarketplaceSearch.Variables
>(combinedMarketplaceSearchQuery, {
  options: (props) => {
    const adTilesCount = (
      props.ad && isExperimentEnabled(props.user, experiments.MARKETPLACE_GRID_ADS) && props.pageName === MParticlePageName.Marketplace
    ) ? 1 : 0;
    return {
      context: {
        headers: {
          'X-Item-Region': props.params.shop ? 'XX' : undefined,
        },
      },
      ssr: false,
      variables: {
        ...mapUrlParams(
          props.params,
          get(props, 'location.query', {}),
          props.user,
          {
            holidaySale: props.pageSettings.holidaySale,
            useExperimentalQuery: isExperimentEnabled(props.user, experiments.LISTINGS_QUERY_REWRITE_V2),
            useExperimentalRecall: props.pageSettings.useExperimentalRecall &&
              isExperimentEnabled(props.user, experiments.SEARCH_RECALL_IMPROVEMENTS),
            useExperimentalBestMatchSignals: props.pageSettings.useExperimentalBestMatchSignals,
          },
          adTilesCount,
        ),
        boostByBumpRate: !props.location?.query?.sort,
        bumpLimit: BUMP_LIMIT,
        bumpOffset: getOffsetFromPage(props.location.query.page, BUMP_LIMIT),
        sort: reverb_search_ListingsSearchRequest_Sort.NONE,
      },
    };
  },
});

export default withUserContext<IExternalProps>(connect(MarketplacePage));
